import { createSelector } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import { withCanonicalLookups } from 'utils/gridColumnLookups';
import { HISTORY_DEFAULT_TIME_RANGE, configCacheKey } from './transactionTablesConfig';

export const selectTableControlSlice = (state) => state.tables;

export const selectAllTablesData = createSelector([selectTableControlSlice], (tableControlSlice) => tableControlSlice?.allTablesData);

export const selectTableIdFetchingDataInProgress = createSelector(
    [selectTableControlSlice],
    (tableControlSlice) => tableControlSlice?.tableIdFetchingDataInProgress
);

export const selectTableConfigFetchingInProgress = createSelector([selectTableControlSlice], (tableControlSlice) => {
    return tableControlSlice?.tableConfigFetchingInProgress;
});

/**
 * Hands out the stored column config as a mutable copy, and the same copy for as long as the stored
 * config is unchanged.
 *
 * Both properties exist for the same reason. A column config whose identity changes is patched into
 * the live grid one option at a time, `column.lookup` included, which breaks the grid - see
 * utils/gridColumnLookups for the mechanism. While this selector memoised on the whole slice it
 * re-cloned on every table-data write, so every refresh handed the grids a new lookup; memoising on
 * the stored entry clones once per fetched config, and withCanonicalLookups shares one lookup instance
 * per distinct list so that two locations offering the same options do not churn either.
 *
 * The copy is what makes that patch survivable rather than fatal in the first place: Redux Toolkit
 * freezes store state, and the patch assigns into the lookup it is given, so handing over the stored
 * object throws `Cannot assign to read only property 'dataSource'`.
 *
 * None of it surfaced while every grid read the same `'ALL'` entry, because the columns option kept
 * the same identity and there was nothing to patch. Now that the key carries the location, a new
 * location hands the grid a different config.
 */
export const selectTableConfigByKey = (configKey) =>
    createSelector([(state, key = configKey) => selectTableControlSlice(state)?.tableConfig?.[key]], (stored) =>
        stored ? withCanonicalLookups(cloneDeep(stored)) : stored
    );

const selectHistoryColumnsConfigMap = createSelector([selectTableControlSlice], (slice) => slice?.historyColumnsConfig);
const selectHistoryColumnsConfigFetchingMap = createSelector([selectTableControlSlice], (slice) => slice?.historyColumnsConfigFetching);
const selectHistoryColumnsConfigErrorMap = createSelector([selectTableControlSlice], (slice) => slice?.historyColumnsConfigError);

// Keyed by location id: the history table's columns are per location. The config is handed out
// as a mutable copy for the same reason as selectTableConfigByKey (the grid writes into it and
// store state is frozen), memoised on the stored entry so the copy's identity is stable.
// The state object is fresh on every recompute, so read it with shallowEqual.
export const selectHistoryColumnsConfigStateByLocationId = (id) => {
    const selectConfig = createSelector([(state, locationId = id) => selectHistoryColumnsConfigMap(state)?.[locationId]], (stored) =>
        stored ? cloneDeep(stored) : stored
    );
    return createSelector(
        [selectConfig, selectHistoryColumnsConfigFetchingMap, selectHistoryColumnsConfigErrorMap, (_, locationId = id) => locationId],
        (columnsConfig, fetching, errors, locationId) => ({
            columnsConfig,
            isLoading: !!fetching?.[locationId],
            error: errors?.[locationId] ?? null,
        })
    );
};

export const selectTableDataById = (id) =>
    createSelector([selectAllTablesData, (_, tableId = id) => tableId], function (allTablesData, tableId) {
        return allTablesData?.[tableId];
    });

// locationId scopes the column config to a location; omit it for cross-location tables.
export const selectTableByIdAndConfigName = (id, configName, locationId) =>
    createSelector([selectTableDataById(id), selectTableConfigByKey(configCacheKey(configName, locationId))], function (table, columnsConfig) {
        let tableData = [];
        if (table) {
            for (let page in table) {
                for (let transaction of table[page].data) {
                    tableData.push({ ...transaction });
                }
            }
        }

        return { tableData, columnsConfig, locationTransactions: table };
    });

const EMPTY_HISTORY_FILTERS = [];
export const selectHistoryQueryByTableId = (id) =>
    createSelector([selectTableControlSlice, (_, tableId = id) => tableId], (tableControlSlice, tableId) => {
        const entry = tableControlSlice?.historyQuery?.[tableId];
        return {
            searchTerm: entry?.searchTerm ?? '',
            timeRange: entry?.timeRange ?? HISTORY_DEFAULT_TIME_RANGE,
            filters: entry?.filters ?? EMPTY_HISTORY_FILTERS,
        };
    });
